/**
 * EXTERNE Lieferverzug-Ansicht — login-los, fuer den Lieferanten.
 * Von glawel-liefertermin und sporttore-liefertermin gemeinsam genutzt.
 *
 * Erreichbar unter /oeffentlich/<programmeId>?token=... (Core-Route
 * PublicProgrammePage). Die Daten kommen von
 * /api/public/<programmeId>/lieferverzug, das denselben Token prueft.
 *
 * Es ist DERSELBE Reiter wie intern (LieferverzugPanel), nur mit anderer
 * Datenquelle und eigenem Kopf — kein zweiter Nachbau, damit die Zahlen
 * garantiert dieselbe Rechnung sind. Weggelassen wird nur die Marken-Karte
 * (`variante="extern"`): sie zeigt Plenty-Stammdaten und liest sich fuer den
 * Lieferanten wie eine Aufteilung nach Lieferant, die es nicht gibt.
 */

import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import LieferverzugPanel from './LieferverzugPanel';
import { useIstSchmal } from './bausteine';
import type { AnalyticsErgebnis } from './typen';

const API_BASIS = (import.meta as any).env?.VITE_API_URL || '/api';

const CI = {
  schwarz: '#1A1A1A',
  gelb: '#E9FF57',
  grau: '#DAD9D9',
  tinteMittel: '#5c5b57',
} as const;

/** Fehler mit einem Text, den das Panel dem Leser direkt zeigen darf. */
function klartextFehler(text: string): Error {
  return Object.assign(new Error(text), { klartext: text });
}

export interface ExternAnsichtProps {
  /** z.B. "glawel-liefertermin" — bestimmt Seite UND Datenroute. */
  programmeId: string;
  /** Ueberschrift, z.B. "Liefertermine GLA-WEL". */
  titel: string;
  /** Ein Satz unter der Ueberschrift: was die Seite zeigt. */
  einleitung: string;
}

export default function ExternAnsicht({ programmeId, titel, einleitung }: ExternAnsichtProps) {
  const token = useMemo(
    () => new URLSearchParams(window.location.search).get('token') ?? '',
    [],
  );

  useEffect(() => {
    document.title = `${titel} · anstoss24`;
  }, [titel]);

  const ladeDaten = useCallback(async (): Promise<AnalyticsErgebnis> => {
    const resp = await fetch(
      `${API_BASIS}/public/${programmeId}/lieferverzug?token=${encodeURIComponent(token)}`,
      { credentials: 'omit' },
    );
    if (resp.status === 401) {
      throw klartextFehler('Dieser Link ist nicht (mehr) gültig. Bitte bei anstoss24 einen neuen anfordern.');
    }
    if (!resp.ok) {
      throw klartextFehler('Die Auswertung ist im Moment nicht abrufbar. Bitte später erneut versuchen.');
    }
    const json = await resp.json();
    if (!json?.success) throw klartextFehler('Die Auswertung konnte nicht berechnet werden.');
    return json.data as AnalyticsErgebnis;
  }, [programmeId, token]);

  return (
    <Rahmen titel={titel} einleitung={einleitung}>
      {token
        ? <LieferverzugPanel programmeId={programmeId} ladeDaten={ladeDaten} variante="extern" />
        : (
          <p style={{ fontSize: '0.9375rem', color: CI.schwarz, lineHeight: 1.7 }}>
            Dieser Seite fehlt der Zugangs-Token. Bitte den vollständigen Link aus der
            E-Mail von anstoss24 verwenden.
          </p>
        )}
    </Rahmen>
  );
}

/**
 * Kopf + Seitenbreite — die interne App liefert das ueber das Core-Layout.
 * Am Handy schrumpfen die Polster: der Lieferant bekommt den Link per Mail und
 * liest ihn oft am Telefon, dort ist jede Pixelbreite Diagramm wert.
 */
function Rahmen({ titel, einleitung, children }: { titel: string; einleitung: string; children: ReactNode }) {
  const schmal = useIstSchmal();
  const seitenPolster = schmal ? '0.75rem' : '1.5rem';
  return (
    <div style={{ minHeight: '100vh', background: '#fbfbfa' }}>
      <header style={{
        borderBottom: `1px solid ${CI.grau}`, background: '#fff',
        padding: schmal ? '1rem 0.75rem' : '1.25rem 1.5rem',
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.375rem' }}>
            <span style={{
              display: 'inline-block', width: '10px', height: '10px',
              background: CI.gelb, borderRadius: '2px',
            }} />
            <span style={{
              fontSize: '0.75rem', letterSpacing: '0.06em',
              color: CI.tinteMittel, fontWeight: 600,
            }}>
              anstoss24 · Lieferanten-Auswertung
            </span>
          </div>
          <h1 style={{ margin: 0, fontSize: schmal ? '1.1875rem' : '1.375rem', fontWeight: 700, color: CI.schwarz }}>
            {titel}
          </h1>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: CI.tinteMittel, lineHeight: 1.6, maxWidth: '70ch' }}>
            {einleitung}
          </p>
        </div>
      </header>

      <main style={{ maxWidth: '1100px', margin: '0 auto', padding: seitenPolster }}>
        {children}
      </main>

      <footer style={{
        maxWidth: '1100px', margin: '0 auto', padding: `0 ${seitenPolster} 2.5rem`,
        fontSize: '0.75rem', color: CI.tinteMittel, lineHeight: 1.6,
      }}>
        anstoss24 · Diese Seite ist nur über den zugesendeten Link erreichbar und
        nicht für die Weitergabe bestimmt.
      </footer>
    </div>
  );
}
